import React, { useState, useEffect, useContext } from 'react';
import client from '@js/modules/httpClient/index';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import { InvestorRegistrationContainer } from '@js/components/common/investorRegistrations/InvestorRegistrationContainer';
import { Representative, AddressType, RepresentativeFormData } from '@js/interfaces/index';
import { CorporateRepresentativeValidation } from '@js/formSettings/validations';
import { Address } from '@js/components/common/AddressRepresentative';
import { usePath } from '@js/hooks/usePath';
import { Context } from '@js/routes';
import { CONFIRM_MESSAGE_IDENTIFICATION_NEEDED } from '@js/components/corporateRegistrations/constants';
import Toast from '@js/modules/toast';
import { SingleInputUnit } from '@js/components/FormUnits/SingleInputUnit';
import { InputField } from '../FormUnits/InputField';

export const CorporateRepresentative = () => {
  const { isEdit, setIsLoading, setIsEdit } = useContext(Context);
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [uploadedFileErrorText, setUploadedFileErrorText] = useState('');
  const [corporate_representative, set_corporate_representative] = useState<Representative>({
    last_name_kanji: '',
    first_name_kanji: '',
    last_name_kana: '',
    first_name_kana: '',
    department: '',
    corporate_relationship: '',
    gender: '',
    birthday_year: '1990',
    birthday_month: '1',
    birthday_day: '1',
    phone_number: ''
  });
  const [address, set_address] = useState<AddressType>({
    postal_code: '',
    prefecture_name: '',
    city: '',
    street: '',
    building: ''
  });
  const methods = useForm<RepresentativeFormData>({
    shouldUnregister: false
  });
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    control
  } = methods;
  const { currentStep, pushNext } = usePath(isEdit);
  const watchPostalCode = watch('postal_code');
  const today = new Date();
  const yearOptions: number[] = [];
  for (let i = 1900; i <= today.getFullYear(); i++) {
    yearOptions[i] = i;
  }
  const monthOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const dateOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
  const onSubmit = (data: RepresentativeFormData) => {
    const {
      last_name_kanji,
      first_name_kanji,
      last_name_kana,
      first_name_kana,
      department,
      corporate_relationship,
      gender,
      birthday_year,
      birthday_month,
      birthday_day,
      phone_number,
      postal_code,
      prefecture_name,
      city,
      street,
      building,
    } = data;

    if (isEdit && !confirm(CONFIRM_MESSAGE_IDENTIFICATION_NEEDED('担当者情報'))) return;

    const formData = new FormData();
    if (data.pdf != null) {
      formData.append('corporate_representative_document[pdf]', data.pdf[0], data.pdf[0].name);
    }
    // pdfは任意だがRails側のparams.require(:corporate_representative_document)は必須なのでdummyを送る
    formData.append('corporate_representative_document[dummy]', 'dummy');

    formData.append('corporate_representative[last_name_kanji]', last_name_kanji);
    formData.append('corporate_representative[first_name_kanji]', first_name_kanji);
    formData.append('corporate_representative[last_name_kana]', last_name_kana);
    formData.append('corporate_representative[first_name_kana]', first_name_kana);
    formData.append('corporate_representative[department]', department);
    formData.append('corporate_representative[corporate_relationship]', corporate_relationship);
    formData.append('corporate_representative[gender]', gender);
    const birthday = `${birthday_year}-${birthday_month}-${birthday_day}`;
    formData.append('corporate_representative[birthday]', birthday);
    formData.append('corporate_representative[phone_number]', phone_number);

    formData.append('address[postal_code]', postal_code);
    formData.append('address[prefecture_name]', prefecture_name);
    formData.append('address[city]', city);
    formData.append('address[street]', street);
    formData.append('address[building]', building);

    client
      .post('/api/v1/customer/corporate_registration/corporate_representative', formData)
      .then((response) => {
        if (isEdit) {
          const message = response.data.message;
          Toast.setFlashNotice(message);
        }
        pushNext();
      })
      .catch((error) => {
        const errorText = error.response?.data?.errors?.[0];
        if (data.pdf != null && errorText?.match(/委任状/)) {
          setUploadedFileErrorText(errorText);
        }
      })
      .finally(() => setIsLoading(false));
  };
  useEffect(() => {
    setIsLoading(true);
    client.get('/api/v1/customer/corporate_registration/corporate_representative')
      .then(response => {
        const data = response.data;
        const corporate_representative = { ...data.corporate_representative };
        set_corporate_representative(corporate_representative);
        set_address(data.address);
        // 委任状が任意項目のためデータが存在する場合のみsetするよ
        if (data.corporate_representative_document.pdf.metadata) {
          setUploadedFileName(data.corporate_representative_document.pdf.metadata.filename);
        }
        setIsEdit(!data['registration_in_progress?']);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setIsLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    const formattedData = { ...corporate_representative, ...address };
    const keys = Object.keys(formattedData) as (keyof typeof formattedData)[];
    keys.forEach((key) => {
      if (formattedData[key]) setValue(key, formattedData[key]);
    });
    setIsLoading(false);
  }, [corporate_representative, address, setValue, setIsLoading]);

  return (
    <InvestorRegistrationContainer formId={currentStep.name} isEdit={isEdit} >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="form-wrap mt40" id={currentStep.name}>
          <fieldset className="form-input__wrap">
            <legend className="form-label">
              <span className="form-label__required">必須</span>お名前
            </legend>
            <div className="form-column">
              <label className="form-column__item">
                <span className="visually-hidden">姓</span>
                <input
                  {...register('last_name_kanji', CorporateRepresentativeValidation.lastNameKanji)}
                  type="text"
                  className={`form-input ${errors.last_name_kanji && 'is-error'}`}
                  placeholder="例）山田"
                  aria-invalid={errors ? 'true' : 'false'}
                />
              </label>
              <label className="form-column__item">
                <span className="visually-hidden">名</span>
                <input
                  {...register('first_name_kanji', CorporateRepresentativeValidation.firstNameKanji)}
                  type="text"
                  className={`form-input ${errors.first_name_kanji && 'is-error'}`}
                  placeholder="例）太郎"
                  aria-invalid={errors.first_name_kanji ? 'true' : 'false'}
                />
              </label>
            </div>
            <ErrorMessage
              errors={errors}
              name="last_name_kanji"
              render={({ message }) =>
                <span className="form-input__error">姓は{message}</span>}
            />
            <ErrorMessage
              errors={errors}
              name="first_name_kanji"
              render={({ message }) =>
                <span className="form-input__error">名は{message}</span>}
            />
          </fieldset>
          <fieldset className="form-input__wrap">
            <legend className="form-label">
              <span className="form-label__required">必須</span>お名前（カタカナ）
            </legend>
            <div className="form-column">
              <label className="form-column__item">
                <span className="visually-hidden">姓（カタカナ）</span>
                <input
                  {...register('last_name_kana', CorporateRepresentativeValidation.lastNameKana)}
                  type="text"
                  className={`form-input ${errors.last_name_kana && 'is-error'}`}
                  placeholder="例）ヤマダ"
                  aria-invalid={errors.last_name_kana ? 'true' : 'false'}
                />
              </label>
              <label className="form-column__item">
                <span className="visually-hidden">名（カタカナ）</span>
                <input
                  {...register('first_name_kana', CorporateRepresentativeValidation.firstNameKana)}
                  type="text"
                  className={`form-input ${errors.first_name_kana && 'is-error'}`}
                  placeholder="例）タロウ"
                  aria-invalid={errors.last_name_kana ? 'true' : 'false'}
                />
              </label>
            </div>
            <ErrorMessage
              errors={errors}
              name="last_name_kana"
              render={({ message }) =>
                <span className="form-input__error">姓（カタカナ）は{message}</span>}
            />
            <ErrorMessage
              errors={errors}
              name="first_name_kana"
              render={({ message }) =>
                <span className="form-input__error">名（カタカナ）は{message}</span>}
            />
          </fieldset>
          <Controller
            control={control}
            name="pdf"
            render={({ field: { onChange, value } }) => (
              <>
                <div className="form-input__wrap">
                  <label className="form-label" htmlFor="pdf">
                    <span className="form-label__required">必須</span>委任状 (代表者自らが担当者の場合は不要です。)
                  </label>
                  <div className="pb8 mt16">
                    <div className="form-file__wrap">
                      <span className="button-short-base--line form-file__button">
                        <input
                          onChange={e => { if (e && e.target && e.target.files && e.target.files.length > 0) onChange(e.target.files); }}
                          className={`form-file form-file--corporate ${errors.pdf && 'is-error'}`}
                          type="file"
                          accept=".pdf"
                          name="pdf"
                          id="pdf"
                        />
                        ファイルを選択する
                      </span>
                      <div className="form-file__name">{(value && value[0].name) || uploadedFileName || '選択されていません'}</div>
                    </div>
                    <div className='form-input__error'>{uploadedFileErrorText}</div>
                  </div>
                  <ul className="list-short-notes">
                    <li className="list-short-notes__item">※pdfファイルのみアップロード可能です</li>
                    <li className="list-short-notes__item">※ファイルサイズは5MB以下にしてください</li>
                  </ul>
                </div>
              </>
            )}
          />
          <InputField
            name="department"
            label="部署"
          >
            <SingleInputUnit
              name="department"
              required={true}
              label="部署"
              inputType="text"
              placeholder="例）総務部"
              validation={CorporateRepresentativeValidation.department}
              initialValue={corporate_representative.department}
            />
          </InputField>
          <fieldset className="form-input__wrap">
            <legend className="form-label">
              <span className="form-label__required">必須</span>担当者と顧客（法人）との関係性
            </legend>
            <div className="form-column">
              {['representative', 'contact_person', 'agent'].map((relationship) => (
                <label key={relationship} className="form-radio__label mr32" htmlFor={`corporate_relationship_${relationship}`}>
                  <input
                    {...register('corporate_relationship', CorporateRepresentativeValidation.corporate_relationship)}
                    type="radio"
                    className={`form-radio__input ${errors.gender}`}
                    value={relationship}
                    id={`corporate_relationship_${relationship}`}
                  />
                  <span className="form-radio__text">
                    {relationship === 'representative' ? '代表者' : relationship === 'contact_person' ? '取引担当者' : '代理人'}
                  </span>
                </label>
              ))}
            </div>
            <ErrorMessage
              errors={errors}
              name="corporate_relationship"
              render={({ message }) => <span className="form-input__error">担当者と法人との関係性は{message}</span>}
            />
          </fieldset>
          <fieldset className="form-input__wrap">
            <legend className="form-label">
              <span className="form-label__required">必須</span>性別
            </legend>
            <div className="form-column">
              <label className="form-radio__label mr32" htmlFor="gender_male">
                <input
                  {...register('gender', CorporateRepresentativeValidation.gender)}
                  type="radio"
                  className={`form-radio__input ${errors.gender}`}
                  value="male"
                  id="gender_male"
                />
                <span className="form-radio__text">男性</span>
              </label>
              <label className="form-radio__label mr32" htmlFor="gender_female">
                <input
                  {...register('gender', CorporateRepresentativeValidation.gender)}
                  type="radio"
                  className={`form-radio__input ${errors.gender}`}
                  value="female"
                  id="gender_female"
                />
                <span className="form-radio__text">女性</span>
              </label>
            </div>
            <ErrorMessage
              errors={errors}
              name="gender"
              render={({ message }) =>
                <span className="form-input__error">性別は{message}</span>}
            />
          </fieldset>
          <fieldset className="form-input__wrap">
            <legend className="form-label">
              <span className="form-label__required">必須</span>生年月日
            </legend>
            <div className="form-column">
              <label className="form-select__wrap form-column__select-date year">
                <span className="visually-hidden">誕生年</span>
                <select
                  {...register('birthday_year', CorporateRepresentativeValidation.birthday)}
                  className="form-select"
                >
                  {yearOptions.map((year) => {
                    return <option value={year} key={year} >{year}</option>;
                  })}
                </select>
              </label>
              <label className="form-select__wrap form-column__select-date">
                <span className="visually-hidden">誕生月</span>
                <select
                  {...register('birthday_month', CorporateRepresentativeValidation.birthday)}
                  className="form-select"
                >
                  {monthOptions.map((month) => {
                    return (
                      <option value={month} key={month} >{month}</option>
                    );
                  })}
                </select>
              </label>
              <label className="form-select__wrap form-column__select-date">
                <span className="visually-hidden">誕生日</span>
                <select
                  {...register('birthday_day', CorporateRepresentativeValidation.birthday)}
                  className="form-select"
                >
                  {dateOptions.map((date) => {
                    return (
                      <option value={date} key={date} >{date}</option>
                    );
                  })}
                </select>
              </label>
            </div>
          </fieldset>
          <div className="form-input__wrap">
            <label className="form-label" htmlFor="phone_number">
              <span className="form-label__required">必須</span>電話番号
            </label>
            <div className="form-column">
              <div className="form-column__item">
                <input
                  {...register('phone_number', CorporateRepresentativeValidation.phoneNumber)}
                  type="tel"
                  id="phone_number"
                  className={`form-input ${errors.phone_number && 'is-error'}`}
                  placeholder="例）0398765432"
                />
              </div>
            </div>
            <ErrorMessage
              errors={errors}
              name="phone_number"
              render={({ message }) =>
                <span className="form-input__error">電話番号は{message}</span>}
            />
            <span className="form-input__note">※ハイフン無しでご入力ください</span>
          </div>
          <Address
            register={register}
            setValue={setValue}
            watchPostalCode={watchPostalCode}
            errors={errors}
          />
        </form>
      </FormProvider>
    </InvestorRegistrationContainer>
  );
};
